import {
  SNAPSHOTS,
  computeConfigDiff,
  type Snapshot,
  type ConfigChange,
  type ConfigSetting,
} from "./audit-data";
import { createDataTable, type Column } from "../ui/data-table";
import { createSeverityBadge } from "./components/severity-badge";
import { confirmModal } from "../ui/modal";
import { showToast } from "../ui/toast";
import type { SnapshotSummaryDto, SnapshotDetailDto } from "./snapshot-api";

const KIND_LABEL: Record<Snapshot["kind"], string> = { auto: "自動", manual: "手動" };
const TRIGGER_LABEL: Record<SnapshotSummaryDto["trigger"], string> = {
  manual: "手動",
  scheduled: "スケジュール",
};
const CHANGE_LABEL: Record<ConfigChange["changeType"], string> = {
  added: "追加",
  removed: "削除",
  modified: "変更",
};

// 実データ（接続時）注入用。snapshots 未注入＝純モック表示。
export interface SnapshotsDeps {
  readonly snapshots?: readonly SnapshotSummaryDto[];
  readonly loadDetail?: (id: string) => Promise<SnapshotDetailDto>;
}

function fmtDateTime(iso: string): string {
  return iso.slice(0, 16).replace("T", " ");
}

// スナップショットの settings({key,value}) を差分計算用の ConfigSetting に変換する。
// label は key、category は既定で identity-access（差分表示は key/value のみ使用）。
function toConfigSettings(settings: readonly { key: string; value: string }[]): ConfigSetting[] {
  return settings.map((s) => ({ key: s.key, label: s.key, value: s.value, category: "identity-access" }));
}

// live モード・モック共通の基本列（種別/設定/旧値/新値）。
// severity 列は live では除外（キーが一致せず常に medium になるため誤解を招く）。
// モック側（renderDiff）はこの後に severity 列を追加する。
function buildChangeColumns(): Column<ConfigChange>[] {
  return [
    { key: "type", header: "種別", cell: (c) => CHANGE_LABEL[c.changeType] },
    { key: "label", header: "設定", cell: (c) => c.label },
    { key: "before", header: "旧値", cell: (c) => c.before ?? "（無し）" },
    { key: "after", header: "新値", cell: (c) => c.after ?? "（無し）" },
  ];
}

// 実データ（接続時）: 実一覧 + 最新2件の差分。書き戻し（ロールバック）は範囲外＝ボタンを出さない。
function buildLiveSnapshots(root: HTMLElement, deps: SnapshotsDeps): void {
  const snapshots = deps.snapshots ?? [];

  if (snapshots.length === 0) {
    const empty = document.createElement("p");
    empty.dataset["testid"] = "snapshot-empty";
    empty.className = "py-6 text-sm text-slate-500";
    empty.textContent =
      "まだスナップショットがありません。「設定チェックを実施する」で取得されます。";
    root.append(empty);
    return;
  }

  // backend は takenAt 降順を保証するが、防御的に再ソートして最新2件を特定する。
  const sorted = [...snapshots].sort((a, b) => b.takenAt.localeCompare(a.takenAt));

  // 一覧
  const listColumns: Column<SnapshotSummaryDto>[] = [
    { key: "takenAt", header: "日時", cell: (s) => fmtDateTime(s.takenAt) },
    { key: "trigger", header: "種別", cell: (s) => TRIGGER_LABEL[s.trigger] },
    { key: "count", header: "設定項目数", cell: (s) => String(s.settingCount) },
    { key: "actorSub", header: "実行者", cell: (s) => s.actorSub },
  ];
  root.append(
    createDataTable<SnapshotSummaryDto>({
      caption: "設定スナップショット一覧",
      columns: listColumns,
      rows: sorted,
      emptyMessage: "スナップショットがありません。",
      rowTestId: "snapshot-row",
    }),
  );

  // 差分セクション
  const diffSection = document.createElement("section");
  diffSection.className = "mt-8";
  const diffHeading = document.createElement("h2");
  diffHeading.className = "text-lg font-medium text-slate-900";
  diffHeading.textContent = "設定差分（最新2件）";
  const diffHost = document.createElement("div");
  diffHost.className = "mt-3";
  diffSection.append(diffHeading, diffHost);
  root.append(diffSection);

  if (sorted.length < 2 || deps.loadDetail === undefined) {
    const needTwo = document.createElement("p");
    needTwo.dataset["testid"] = "snapshot-need-two";
    needTwo.className = "py-6 text-sm text-slate-500";
    needTwo.textContent = "差分の比較には2件以上のスナップショットが必要です。";
    diffHost.append(needTwo);
    return;
  }

  const loading = document.createElement("p");
  loading.dataset["testid"] = "snapshot-diff-loading";
  loading.className = "py-6 text-sm text-slate-500";
  loading.textContent = "差分を計算中...";
  diffHost.append(loading);

  const loadDetail = deps.loadDetail;
  const newId = sorted[0]!.snapshotId;
  const oldId = sorted[1]!.snapshotId;

  void (async () => {
    try {
      const [curr, prev] = await Promise.all([loadDetail(newId), loadDetail(oldId)]);
      const changes = computeConfigDiff(toConfigSettings(prev.settings), toConfigSettings(curr.settings));
      diffHost.replaceChildren(
        createDataTable<ConfigChange>({
          caption: "スナップショット間の設定差分",
          columns: buildChangeColumns(),
          rows: changes,
          emptyMessage: "差分はありません。",
          rowTestId: "change-row",
        }),
      );
    } catch {
      diffHost.replaceChildren();
      const err = document.createElement("p");
      err.dataset["testid"] = "snapshot-diff-error";
      err.className = "py-6 text-sm text-rose-600";
      err.textContent = "差分の取得に失敗しました。時間をおいて再度お試しください。";
      diffHost.append(err);
      showToast({
        tone: "warning",
        title: "差分の取得に失敗しました",
        message: "時間をおいて再度お試しください。",
      });
    }
  })();
}

export function renderSnapshots(root: HTMLElement, deps?: SnapshotsDeps): void {
  root.replaceChildren();

  // snapshots 未注入 = 純モック（未接続 / apiClient なし）→ 従来モック表示。
  if (deps?.snapshots === undefined) {
    renderMockSnapshots(root);
    return;
  }

  buildLiveSnapshots(root, deps);
}

// 従来モック表示（純モック / 未接続）。SNAPSHOTS ベースで取得ボタン + pickers + ロールバック。
function renderMockSnapshots(root: HTMLElement): void {
  let snapshots: Snapshot[] = [...SNAPSHOTS].sort((a, b) => b.takenAt.localeCompare(a.takenAt));
  let manualSeq = 0;
  let baseId = snapshots[1]?.id ?? snapshots[0]!.id;
  let targetId = snapshots[0]!.id;

  // 上部コントロール
  const controls = document.createElement("div");
  controls.className = "mb-4 flex items-center gap-4";
  const autoNote = document.createElement("p");
  autoNote.className = "text-sm text-slate-600";
  autoNote.textContent = "自動取得: スケジュールに従って自動取得（スケジュールタブで設定）";
  const takeBtn = document.createElement("button");
  takeBtn.type = "button";
  takeBtn.className = "rounded bg-shinonome-700 px-3 py-2 text-sm font-medium text-white hover:bg-shinonome-800";
  takeBtn.textContent = "今すぐ取得";
  takeBtn.addEventListener("click", () => {
    manualSeq += 1;
    const newest = snapshots[0]!;
    snapshots = [
      {
        id: "snap-manual-" + manualSeq,
        takenAt: "2026-06-09T03:0" + manualSeq + ":00Z",
        kind: "manual",
        takenBy: "test-admin@soramie.jp",
        settings: newest.settings,
      },
      ...snapshots,
    ];
    renderList();
    renderPickers();
  });
  controls.append(autoNote, takeBtn);

  const listHost = document.createElement("div");
  listHost.className = "mb-8";

  const diffSection = document.createElement("section");
  const diffHeading = document.createElement("h2");
  diffHeading.className = "text-lg font-medium text-slate-900";
  diffHeading.textContent = "設定差分";
  const pickers = document.createElement("div");
  pickers.className = "mt-2 flex flex-wrap items-end gap-3";
  const rollbackStatus = document.createElement("p");
  rollbackStatus.setAttribute("role", "status");
  rollbackStatus.className = "mt-2 text-sm text-slate-700";
  const diffHost = document.createElement("div");
  diffHost.className = "mt-3";
  diffSection.append(diffHeading, pickers, rollbackStatus, diffHost);

  function findSnap(id: string): Snapshot {
    return snapshots.find((s) => s.id === id) ?? snapshots[0]!;
  }

  function buildPicker(id: string, selectedId: string, onChange: (id: string) => void): HTMLSelectElement {
    const sel = document.createElement("select");
    sel.id = id;
    sel.className = "mt-1 rounded border border-slate-300 px-2 py-1 text-sm";
    for (const s of snapshots) {
      const opt = document.createElement("option");
      opt.value = s.id;
      opt.textContent = s.takenAt.slice(0, 16).replace("T", " ") + "（" + KIND_LABEL[s.kind] + "）";
      if (s.id === selectedId) opt.selected = true;
      sel.append(opt);
    }
    sel.addEventListener("change", () => onChange(sel.value));
    return sel;
  }

  function renderPickers(): void {
    pickers.replaceChildren();
    const baseWrap = document.createElement("div");
    const baseLabel = document.createElement("label");
    baseLabel.className = "block text-xs font-medium text-slate-600";
    baseLabel.htmlFor = "snap-base";
    baseLabel.textContent = "比較元（古い）";
    baseWrap.append(baseLabel, buildPicker("snap-base", baseId, (id) => { baseId = id; renderDiff(); }));
    const targetWrap = document.createElement("div");
    const targetLabel = document.createElement("label");
    targetLabel.className = "block text-xs font-medium text-slate-600";
    targetLabel.htmlFor = "snap-target";
    targetLabel.textContent = "比較先（新しい）";
    targetWrap.append(targetLabel, buildPicker("snap-target", targetId, (id) => { targetId = id; renderDiff(); }));
    pickers.append(baseWrap, targetWrap);
  }

  function buildRollbackControl(change: ConfigChange): HTMLElement {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "rounded border border-slate-300 px-2 py-1 text-xs text-slate-700 hover:bg-slate-100";
    btn.textContent = "ロールバック";
    btn.addEventListener("click", () => {
      confirmModal({
        title: "ロールバックの確認",
        message:
          "「" + change.label + "」を " + (change.before ?? "（無し）") +
          " に戻します。これは Salesforce への書き戻し＝高権限操作として監査されます（§3.2 PRIVILEGED_ACTION）。",
        confirmLabel: "ロールバック実行",
        tone: "danger",
        onConfirm: () => {
          rollbackStatus.textContent =
            "「" + change.label + "」をロールバックしました（サンプル）。高権限操作として監査されます（§3.2 PRIVILEGED_ACTION）。";
          showToast({ tone: "warning", title: "ロールバックを実行しました", message: "（サンプル）高権限操作として監査されます。" });
        },
      });
    });
    return btn;
  }

  function renderDiff(): void {
    const changes = computeConfigDiff(findSnap(baseId).settings, findSnap(targetId).settings);
    const columns: Column<ConfigChange>[] = [
      ...buildChangeColumns(),
      { key: "sev", header: "重大度影響", cell: (c) => createSeverityBadge(c.severityImpact) },
      { key: "op", header: "操作", cell: (c) => buildRollbackControl(c) },
    ];
    diffHost.replaceChildren(
      createDataTable<ConfigChange>({
        caption: "スナップショット間の設定差分",
        columns,
        rows: changes,
        emptyMessage: "差分はありません。",
        rowTestId: "change-row",
      }),
    );
  }

  function renderList(): void {
    const columns: Column<Snapshot>[] = [
      { key: "takenAt", header: "日時", cell: (s) => s.takenAt.slice(0, 16).replace("T", " ") },
      { key: "kind", header: "種別", cell: (s) => KIND_LABEL[s.kind] },
      { key: "takenBy", header: "取得者", cell: (s) => s.takenBy },
      { key: "count", header: "設定項目数", cell: (s) => String(s.settings.length) },
      {
        key: "changes",
        header: "前回からの変更",
        cell: (s) => {
          const idx = snapshots.indexOf(s);
          const older = snapshots[idx + 1];
          if (older === undefined) return "-";
          return computeConfigDiff(older.settings, s.settings).length + " 件";
        },
      },
    ];
    listHost.replaceChildren(
      createDataTable<Snapshot>({
        caption: "設定スナップショット一覧",
        columns,
        rows: snapshots,
        emptyMessage: "スナップショットがありません。",
        rowTestId: "snapshot-row",
      }),
    );
  }

  renderList();
  renderPickers();
  renderDiff();
  root.append(controls, listHost, diffSection);
}
